import { useApolloClient } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { UnifiedWorkItemsQuery } from "generated-graphql/graphql";
import {
  GridSelectionProvider,
  OmnisearchDataGridV2,
  useOmnisearchState,
} from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useState } from "react";

import { UNIFIED_WORK_QUERY } from "./api";
import { ADDITIONAL_FILTER_KEYS, COLUMNS } from "./columns";
import { NewWorkDialog } from "./NewWorkDialog";
import { UnifiedWorkRow } from "./types";
import { useWorkDrawer } from "./useWorkDrawer";
import { WorkBulkActions } from "./WorkBulkActions";
import { WorkDrawer } from "./WorkDrawer";
import { WorkStats } from "./WorkStats";

const getItems = (data: UnifiedWorkItemsQuery): UnifiedWorkRow[] => data.unifiedWorkItems.items;
const getCount = (data: UnifiedWorkItemsQuery): number => data.unifiedWorkItems.count;

const INITIAL_SORT: GridSortModel = [{ field: "dueAt", sort: "asc" }];

// The MVP surface is the list, full stop: work type is a facet in the filter
// module (no chip row), and rows carry due date + estimate.
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Work",
    fields: ["workType", "workflowStatus", "activityStatus", "assigneeEmail", "tag"],
  },
  {
    name: "Where",
    fields: ["tenant", "facilityName", "state"],
  },
];

/**
 * "Assigned to me" toggle, same behavior as the Generator Status Changes
 * dashboard's: a Switch that flips the `assigneeEmail` omnisearch key — the
 * same key the Assignee column's staff picker uses — to the current user's
 * email (the picker serializes staff users to email).
 */
function AssignedToMeToggle() {
  const { user } = useCurrentUser();
  const { getOmnisearchValue, editOmnisearchKeyValues, removeOmnisearchKeyValue } =
    useOmnisearchState();
  const email = user?.email;
  const active = Boolean(email) && getOmnisearchValue("assigneeEmail") === email;
  return (
    <FormControlLabel
      sx={{ marginRight: 0, whiteSpace: "nowrap" }}
      control={
        <Switch
          checked={active}
          disabled={!email}
          onChange={(_, checked) =>
            checked && email
              ? editOmnisearchKeyValues([["assigneeEmail", email]])
              : removeOmnisearchKeyValue("assigneeEmail")
          }
        />
      }
      label="Assigned to me"
    />
  );
}

/**
 * The unified work queue: every work type that instantiates workflows, in one
 * staff grid — Tier II reports, cohorts, and 8700-12 waste submissions today;
 * detected-work types join via stub workflows in the second wave. Clicking a
 * row opens the {@link WorkDrawer} inspector on the Workflow wrapper itself,
 * which then links out to the per-type surface where the work is done;
 * "+ New work" creates a freeform Custom Deliverable, and the grid is built
 * for bulk triage: checkbox multi-select with one-click (re)assignment.
 */
export function UnifiedWork() {
  useBreadcrumb({ label: "Work" });
  const apolloClient = useApolloClient();
  const [composeOpen, setComposeOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useLocalStorageState<boolean>("unifiedWorkStatsOpen", {
    defaultValue: true,
  });

  // Row click opens the workflow inspector; its identity lives in the URL.
  const drawer = useWorkDrawer();

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const handleCreated = useCallback(() => {
    apolloClient.refetchQueries({ include: ["UnifiedWorkItems", "UnifiedWorkStats"] });
  }, [apolloClient]);

  const commandButtons = useMemo(
    () => [
      <AssignedToMeToggle key="assigned-to-me" />,
      // The Bulk Actions dropdown, the pattern the other staff grids use.
      <WorkBulkActions key="bulk-actions" />,
      <Button
        key="new-work"
        variant="contained"
        startIcon={<Add />}
        onClick={() => setComposeOpen(true)}
      >
        New work
      </Button>,
    ],
    []
  );

  return (
    <GridSelectionProvider>
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
        {/* No page title: the app header's breadcrumb already reads "Work". */}
        <Button
          variant="text"
          size="small"
          sx={{ alignSelf: "flex-end" }}
          onClick={() => setStatsOpen((prev) => !prev)}
        >
          {statsOpen ? "Hide Stats" : "Show Stats"}
        </Button>
        <WorkStats open={statsOpen} />
        <OmnisearchDataGridV2
          query={UNIFIED_WORK_QUERY}
          persistenceKey="unified-work"
          columns={COLUMNS}
          getItems={getItems}
          getCount={getCount}
          buildVariables={buildVariables}
          onRowClick={drawer.openRow}
          onItemsLoaded={drawer.onRowsLoaded}
          initialSortModel={INITIAL_SORT}
          filterGroups={FILTER_GROUPS}
          additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
          commandButtons={commandButtons}
          checkboxSelection
          disableRowSelectionOnClick
          // The page Box already provides p:3 (header + command strip align
          // to it); zero the grid's built-in page padding so the search bar
          // and grid share the same left/right edges.
          sx={{ pt: 0, px: 0 }}
          noRowsMessage="No open work matches this view."
        />
        <NewWorkDialog
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          onCreated={handleCreated}
        />
        <WorkDrawer workflowId={drawer.workflowId} row={drawer.row} onClose={drawer.close} />
      </Box>
    </GridSelectionProvider>
  );
}
